import React, { useEffect } from "react";
import {
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Button,
  Typography,
} from "antd";
import dayjs from "dayjs";
import AppModal from "../../modals/AppModal";

type FieldType = "text" | "number" | "date" | "option_list";

type DescriptorFieldSlim = {
  id: string;
  name: string;
  type: FieldType | string;
  optionsFull?: Array<{ id: string; option: string }>;
};

type Mode = "add" | "edit";

type Props = {
  open: boolean;
  loading?: boolean;
  mode?: Mode;
  onCancel: () => void;
  onAdd?: (values: Record<string, any>) => void;
  onEdit?: (values: Record<string, any>) => void;
  fields: DescriptorFieldSlim[];
  initialValues?: { [fieldId: string]: any } | null;
  className?: string;
  mediaTitle?: string;
};

const AddDescriptorModal: React.FC<Props> = ({
  open,
  loading,
  mode = "add",
  onCancel,
  onAdd,
  onEdit,
  fields,
  initialValues,
  className,
  mediaTitle,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open) return;

    form.resetFields();

    if (initialValues) {
      const mapped: any = {};

      if (initialValues.weight !== undefined) {
        mapped.weight = initialValues.weight;
      }

      for (const f of fields) {
        const raw = initialValues[f.id];

        if (raw === undefined || raw === null) continue;

        if (f.type === "date" && raw) {
          mapped[f.id] = dayjs(raw);
        } else {
          mapped[f.id] = raw;
        }
      }

      form.setFieldsValue(mapped);
    }
  }, [open, form, fields, initialValues]);

  const handleSubmit = async () => {
    const v = await form.validateFields();

    const payload: Record<string, any> = {
      weight: v.weight ?? 0,
    };

    for (const f of fields) {
      const val = v[f.id];

      if (val === undefined) continue;

      if (f.type === "date" && val) {
        payload[f.id] = dayjs.isDayjs(val) ? val.toDate().toISOString() : val;
      } else if (f.type === "number") {
        payload[f.id] = typeof val === "number" ? val : Number(val);
      } else if (f.type === "option_list") {
        payload[f.id] = Array.isArray(val) ? val : [val].filter(Boolean);
      } else {
        payload[f.id] = val;
      }
    }

    if (mode === "edit" && onEdit) return onEdit(payload);
    if (mode === "add" && onAdd) return onAdd(payload);
  };

  return (
    <AppModal
      open={open}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      title={mode === "edit" ? "Edit descriptor" : "Add descriptor"}
      width={720}
      className={className}
      footer={
        <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
          <Button onClick={onCancel} className="cancel-button">
            Cancel
          </Button>
          <Button
            className="add-button"
            loading={loading}
            onClick={handleSubmit}
          >
            {mode === "edit" ? "Save changes" : "Add"}
          </Button>
        </div>
      }
    >
      {fields.length === 0 ? (
        <Typography.Paragraph type="secondary">
          Select a set and create fields first.
        </Typography.Paragraph>
      ) : (
        <Form form={form} layout="vertical" initialValues={{ weight: 0 }}>
          <Form.Item label="Media title">
            <Input value={mediaTitle || ""} placeholder="Title" disabled />
          </Form.Item>

          {fields.map((f) => {
            const name = f.id;

            if (f.type === "text") {
              return (
                <Form.Item key={f.id} label={f.name} name={name}>
                  <Input />
                </Form.Item>
              );
            }

            if (f.type === "number") {
              return (
                <Form.Item key={f.id} label={f.name} name={name}>
                  <InputNumber style={{ width: "100%" }} />
                </Form.Item>
              );
            }

            if (f.type === "date") {
              return (
                <Form.Item key={f.id} label={f.name} name={name}>
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              );
            }

            const opts =
              (f.optionsFull || []).map((o) => ({
                label: o.option,
                value: o.id,
              })) || [];

            return (
              <Form.Item key={f.id} label={f.name} name={name}>
                <Select
                  mode="multiple"
                  placeholder="Select options"
                  options={opts}
                />
              </Form.Item>
            );
          })}

          <Form.Item label="Weight" name="weight">
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              placeholder="Enter weight"
            />
          </Form.Item>
        </Form>
      )}
    </AppModal>
  );
};

export default AddDescriptorModal;
